import { useState, useEffect, useMemo, useTransition } from 'react'
import { Link } from 'react-router-dom'
import { useInView } from 'react-intersection-observer'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Navbar } from '@/components/navbar'
import { NewsCard, NewsCardSkeleton } from '@/components/news-card'
import { Button } from '@/components/ui/button'
import { newsApi } from '@/services/api'
import { TrendingUp, Loader2, Flame } from 'lucide-react'
import { motion } from 'framer-motion'
import { getViewMode } from '@/lib/utils'

export function TrendingPage() {
  const [viewMode, setViewMode] = useState(getViewMode())
  const [isPending, startTransition] = useTransition()

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: '200px',
  })

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: ['trending'],
    queryFn: ({ pageParam = 1 }) => newsApi.getTrending(pageParam),
    getNextPageParam: (lastPage) => {
      if (!lastPage.data.next) return undefined
      
      // Extract page number from URL
      try {
        const url = new URL(lastPage.data.next)
        const page = url.searchParams.get('page')
        return page ? parseInt(page, 10) : undefined
      } catch {
        return undefined
      }
    },
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  })

  const articles = useMemo(() => {
    return data?.pages.flatMap(page => page.data.results) || []
  }, [data?.pages])

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      startTransition(() => {
        fetchNextPage()
      })
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar onViewModeChange={setViewMode} />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="h-8 w-24 bg-gray-200 rounded animate-shimmer mb-6" />
            <div className="bg-white rounded-xl p-8 mb-8">
              <div className="flex items-center gap-6">
                <div className="w-24 h-24 bg-gray-200 rounded-full animate-shimmer" />
                <div className="flex-1 space-y-3">
                  <div className="h-8 bg-gray-200 rounded animate-shimmer w-1/2" />
                  <div className="h-4 bg-gray-200 rounded animate-shimmer w-3/4" />
                </div>
              </div>
            </div>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <NewsCardSkeleton key={i} variant={viewMode} />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar onViewModeChange={setViewMode} />
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="bg-red-50 rounded-full p-8 mb-6">
              <svg className="w-24 h-24 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Something went wrong</h3>
            <p className="text-gray-600 text-center max-w-md mb-6">
              We couldn't load the trending articles. Please try again.
            </p>
            <Button onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar onViewModeChange={setViewMode} />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Back button */}
          <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-8 mb-8 text-white"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                <Flame className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl font-bold mb-2">Trending Now</h1>
                <p className="text-white/90">
                  {articles.length > 0 
                    ? `${articles.length}+ trending stories`
                    : 'Discover what\'s hot right now'
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-white/80">
              <TrendingUp className="w-4 h-4" />
              <span>Most popular articles across all sources</span>
            </div>
          </motion.div>

          {/* Empty state */}
          {articles.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-16 bg-white rounded-xl shadow-sm"
            >
              <div className="bg-gray-100 rounded-full p-8 w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                <Flame className="w-12 h-12 text-gray-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">No Trending Articles</h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                There are no trending articles at the moment. Check back later!
              </p>
              <Link to="/">
                <Button>
                  Explore All Articles
                </Button>
              </Link>
            </motion.div>
          ) : (
            <div className="space-y-4">
              {articles.map((article, index) => (
                <NewsCard
                  key={article.alias || article.id}
                  article={article}
                  variant={viewMode}
                  index={index}
                />
              ))}
              
              {/* Loading next page skeletons */}
              {(isFetchingNextPage || isPending) && (
                <>
                  {[...Array(3)].map((_, i) => (
                    <NewsCardSkeleton key={`skeleton-${i}`} variant={viewMode} />
                  ))}
                </>
              )}
            </div>
          )}

          {/* Load more trigger */}
          <div ref={loadMoreRef} className="py-4">
            {!hasNextPage && articles.length > 0 && (
              <p className="text-sm text-gray-500 text-center">You've reached the end</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
